import { useMemo } from 'react';

import { useAuth } from '@src/hooks/use-auth';
import { useGetCurrentSubscriptionQuery, useGetProfileQuery } from '@src/store/queries/users';
import { bypassesPlanRestrictions } from '@src/utils/constants';

const ACTIVE_STATUSES = ['active', 'trialing'];

/**
 * Check if user has an active app-level trial (separate from Stripe subscription trial).
 */
const isAppTrialActive = (profile) => {
  if (!profile?.trialExpiresAt) return false;

  return new Date(profile.trialExpiresAt) > new Date();
};

/**
 * Calculate days remaining in trial.
 */
const getTrialDaysRemaining = (profile) => {
  if (!profile?.trialExpiresAt) return 0;
  const diff = new Date(profile.trialExpiresAt) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

/**
 * Hook to check user's subscription status
 *
 * @returns {Object} Subscription status info
 * @property {boolean} isLoading - True if data is still loading
 * @property {boolean} hasActiveSubscription - True if user has active/trialing subscription
 * @property {boolean} isTrialing - True if user is on a Stripe trial
 * @property {boolean} isOnAppTrial - True if user is on an app-level trial (7 or 14-day)
 * @property {number} trialDaysRemaining - Days left in app trial
 * @property {string|null} trialType - 'no-card' or 'card' or null
 * @property {boolean} bypassesRestrictions - True if user bypasses subscription (ADMIN/INVITED)
 * @property {boolean} canAccessPaidFeatures - True if user can access paid features
 * @property {Object|null} subscription - Current subscription data
 * @property {Object|null} plan - Current plan data (if subscription exists)
 * @property {number|null} planLevel - Current plan level (null if no subscription)
 */
export const useSubscriptionStatus = () => {
  const { isAuthenticated } = useAuth();
  const { data: profile, isLoading: profileLoading } = useGetProfileQuery(undefined, { skip: !isAuthenticated });
  const { data: subscription, isLoading: subscriptionLoading } = useGetCurrentSubscriptionQuery(undefined, {
    skip: !isAuthenticated || !profile?.stripeCustomerId,
  });

  return useMemo(() => {
    const isLoading = profileLoading || (profile?.stripeCustomerId && subscriptionLoading);

    const bypasses = bypassesPlanRestrictions(profile?.role);

    const hasActiveSubscription = subscription && ACTIVE_STATUSES.includes(subscription.status);
    const isTrialing = subscription?.status === 'trialing';
    const isOnAppTrial = isAppTrialActive(profile);
    const trialDaysRemaining = getTrialDaysRemaining(profile);
    const trialType = profile?.trialType ?? null;

    const canAccessPaidFeatures = bypasses || hasActiveSubscription || isOnAppTrial;

    const planLevel = hasActiveSubscription ? (subscription?.planId?.level ?? null) : isOnAppTrial ? 1 : null;

    return {
      isLoading,
      hasActiveSubscription: !!hasActiveSubscription,
      isTrialing,
      isOnAppTrial,
      trialDaysRemaining,
      trialType,
      bypassesRestrictions: bypasses,
      canAccessPaidFeatures,
      subscription: subscription ?? null,
      plan: subscription?.planId ?? null,
      planLevel,
    };
  }, [profile, subscription, profileLoading, subscriptionLoading]);
};

/**
 * Hook to check if user has completed onboarding.
 * Requires both finished flag AND a personalized plan to exist.
 */
export const useOnboardingStatus = () => {
  const { isAuthenticated } = useAuth();
  const { data: profile, isLoading } = useGetProfileQuery(undefined, { skip: !isAuthenticated });

  return useMemo(
    () => ({
      isLoading,
      isOnboardingComplete: (profile?.onboarding?.finished ?? false) && Boolean(profile?.personalizedPlan),
      onboardingStep: profile?.onboarding?.onboardingStep ?? 1,
    }),
    [profile, isLoading],
  );
};

/**
 * Combined hook for checking access to protected routes.
 *
 * `hasProfile` indicates whether the server-side profile has been fetched —
 * use this for data-readiness checks, not for authentication gating (use
 * `useAuth().isAuthenticated` for that).
 */
export const useAccessStatus = () => {
  const { isAuthenticated } = useAuth();
  const { data: profile, isLoading: profileLoading } = useGetProfileQuery(undefined, { skip: !isAuthenticated });
  const subscriptionStatus = useSubscriptionStatus();
  const onboardingStatus = useOnboardingStatus();

  return useMemo(() => {
    const isLoading = profileLoading || subscriptionStatus.isLoading || onboardingStatus.isLoading;

    let redirectTo = null;

    if (!onboardingStatus.isOnboardingComplete) {
      redirectTo = '/onboarding';
    } else if (!subscriptionStatus.canAccessPaidFeatures) {
      redirectTo = '/pricing';
    }

    return {
      isLoading,
      hasProfile: !!profile,
      isOnboardingComplete: onboardingStatus.isOnboardingComplete,
      canAccessPaidFeatures: subscriptionStatus.canAccessPaidFeatures,
      bypassesRestrictions: subscriptionStatus.bypassesRestrictions,
      redirectTo,
      profile,
      ...subscriptionStatus,
    };
  }, [profile, profileLoading, subscriptionStatus, onboardingStatus]);
};
